import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { sseClient } from '../api/sse'
import type { SSEEvent } from '../types'
import { getEventType } from '../types'
import { useForecastStore } from '../store/forecastStore'
import { getForecast } from '../api/client'
import { urlHostname } from '../utils/text'

const SSE_DEBUG = false
const sseLog = (...args: any[]) => {
  if (SSE_DEBUG) console.log(...args)
}
const sseWarn = (...args: any[]) => {
  if (SSE_DEBUG) console.warn(...args)
}

export function useSSE(jobId: string | null) {
  const navigate = useNavigate()
  const unsubscribeRef = useRef<(() => void)[]>([])
  const { 
    setCurrentJobId,
    setCurrentQuestionId, 
    setAgentPredictions,
    setCurrentRuns,
    updateAgentState,
    setSupervisorReasoning,
    setSupervisorSearches,
    setSupervisorVisitedDomains,
    setSupervisorStageStarted,
    setPredictionStageStarted,
    setPredictionStageComplete,
    setSupervisorStageComplete,
    setFinalStageComplete,
    setFormatterStatus,
    setFormatterPreview,
    setFormatterSearch,
  } = useForecastStore()

  useEffect(() => {
    if (!jobId) return

    // New stream for a (potentially) new run: reset supervisor stage flag
    setSupervisorStageStarted(false)
    setPredictionStageStarted(false)
    setPredictionStageComplete(false)
    setSupervisorStageComplete(false)
    setFinalStageComplete(false)
    setFormatterStatus('reformatting')
    setFormatterPreview(null)
    setFormatterSearch({ status: 'idle' })
    setSupervisorSearches([])
    setSupervisorVisitedDomains([])

    sseClient.connect(jobId)

    // Handle forecast complete
    const unsub1 = sseClient.on('forecast_complete', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'forecast_complete' && event.data) {
        sseLog('[SSE] Forecast complete:', event.data)
        setFinalStageComplete(true)
        if (event.data.question_id) {
          setCurrentQuestionId(event.data.question_id)
          // Navigate to results page
          navigate(`/results/${event.data.question_id}`)
        }
        if (event.data.agent_predictions) {
          setAgentPredictions(event.data.agent_predictions)
        }

        // Upsert the completed run into currentRuns so charts/cards update without a refresh
        if (jobId) {
          void (async () => {
            try {
              const run = await getForecast(jobId)
              const store = useForecastStore.getState()
              const existing = store.currentRuns
              const idx = existing.findIndex((r) => r.job_id === run.job_id)
              const next =
                idx >= 0
                  ? existing.map((r) => (r.job_id === run.job_id ? run : r))
                  : [run, ...existing]
              setCurrentRuns(next)
            } catch (e) {
              sseWarn('[SSE] Failed to refresh completed run data:', e)
            }
          })()
        }

        // IMPORTANT: once a run is complete, clear the active job so the Home page doesn't
        // keep showing the formatter/progress panel when you navigate back.
        setCurrentJobId(null)
        setFormatterStatus('idle')
        setFormatterPreview(null)
        setFormatterSearch({ status: 'idle' })
      }
    })

    // Handle formatter preview
    const unsub2 = sseClient.on('formatter_preview', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'formatter_preview') {
        sseLog('[SSE] Formatter preview received:', event.data)
        sseLog('[SSE] Full event:', event)
        
        // Extract output and question_id
        const output = event.data?.output
        const questionId = event.data?.question_id
        
        // Store question_id if provided
        if (questionId) {
          sseLog('[SSE] Setting question_id from preview:', questionId)
          setCurrentQuestionId(questionId)
        }
        
        sseLog('[SSE] Extracted output:', output)
        sseLog('[SSE] Output keys:', output ? Object.keys(output) : 'no output')
        
        if (output && (output.binary_question || Object.keys(output).length > 0)) {
          setFormatterPreview({ question_id: questionId, output })
          setFormatterStatus('ready')
        } else {
          sseWarn('[SSE] No valid output data in formatter_preview event', event.data)
          setFormatterStatus('error')
        }
      }
    })

    // Handle stage started (prediction stage means agents are starting)
    const unsub3a = sseClient.on('stage_started', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'stage_started') {
        sseLog('[SSE] Stage started:', event.stage, event.data)
        if (event.stage === 'prediction') {
          sseLog('[SSE] Prediction stage started - agents will begin soon')
          setPredictionStageStarted(true)
          // Initialize all agents to pending if not already initialized
          const numAgents = event.data?.num_agents || 6
          const store = useForecastStore.getState()
          for (let i = 1; i <= numAgents; i++) {
            if (!store.activeAgentStates[i]) {
              updateAgentState(i, {
                status: 'pending',
                reasoning: '',
                searches: [],
                visited_domains: [],
              })
            }
          }
        }
        if (event.stage === 'supervisor') {
          // Reset supervisor stream for this run
          setSupervisorReasoning('')
          setSupervisorStageStarted(true)
        }
      }
    })

    const unsub3b = sseClient.on('stage_complete', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType !== 'stage_complete') return
      if (event.stage === 'prediction') setPredictionStageComplete(true)
      if (event.stage === 'supervisor') setSupervisorStageComplete(true)
    })

    // Handle agent started
    const unsub3 = sseClient.on('agent_started', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'agent_started' && event.agent_id != null) {
        sseLog('[SSE] Agent started:', event.agent_id, event.data)
        updateAgentState(event.agent_id, {
          status: 'running',
          reasoning: '',
          searches: [],
        })
      }
    })

    // Handle reasoning delta (streaming reasoning updates)
    const unsub4a = sseClient.on('reasoning_delta', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'reasoning_delta' && event.data) {
        // Supervisor reasoning summary comes through with agent_id=null and stage='supervisor'
        if (event.stage === 'supervisor') {
          const current = useForecastStore.getState().supervisorReasoning || ''
          const next = event.data.buffer || current + (event.data.delta || '')
          setSupervisorReasoning(next)
          return
        }

        if (event.agent_id != null) {
          const store = useForecastStore.getState()
          const currentState = store.activeAgentStates[event.agent_id]
          const currentReasoning = currentState?.reasoning || ''
          const newReasoning = event.data.buffer || event.data.delta || ''
          updateAgentState(event.agent_id, {
            reasoning: newReasoning || currentReasoning + (event.data.delta || ''),
          })
        }
      }
    })

    // Handle agent reasoning (final reasoning)
    const unsub4 = sseClient.on('agent_reasoning', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'agent_reasoning' && event.agent_id != null && event.data) {
        sseLog('[SSE] Agent reasoning update:', event.agent_id)
        updateAgentState(event.agent_id, {
          reasoning: event.data.reasoning || '',
        })
      }
    })

    // Handle search started
    const unsub5a = sseClient.on('search_started', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'search_started' && event.data) {
        // Formatter validation (optional) uses search events too.
        if (event.stage === 'formatter') {
          setFormatterSearch({ status: 'searching', query: event.data.query || '' })
          return
        }
        // Supervisor searches have agent_id=null but stage='supervisor'
        if (event.stage === 'supervisor') {
          const q = event.data.query || ''
          if (q) {
            const current = useForecastStore.getState().supervisorReasoning || ''
            const line = `\n⌕ Searching: "${q}"\n`
            setSupervisorReasoning(current + line)
          }
          return
        }

        if (event.agent_id != null) {
          sseLog('[SSE] Search started for agent:', event.agent_id, event.data.query)
        }
      }
    })

    // Handle search complete
    const unsub5b = sseClient.on('search_complete', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'search_complete' && event.data) {
        if (event.stage === 'formatter') {
          setFormatterSearch({
            status: 'complete',
            query: event.data.query || '',
            num_results: typeof event.data.num_results === 'number' ? event.data.num_results : event.data.results?.length,
          })
          return
        }
        // Supervisor searches have agent_id=null but stage='supervisor'
        if (event.stage === 'supervisor') {
          const q = event.data.query || ''
          const results = Array.isArray(event.data.results) ? event.data.results : []

          const store = useForecastStore.getState()
          const nextSearches = [
            ...(store.supervisorSearches || []),
            { query: q, results },
          ]
          setSupervisorSearches(nextSearches)

          const nextDomains = new Set<string>(store.supervisorVisitedDomains || [])
          for (const r of results) {
            if (r?.url) nextDomains.add(urlHostname(r.url))
          }
          setSupervisorVisitedDomains(Array.from(nextDomains))
          return
        }

        if (event.agent_id != null) {
          sseLog('[SSE] Search complete for agent:', event.agent_id, event.data.num_results)
          const store = useForecastStore.getState()
          const currentState = store.activeAgentStates[event.agent_id]
          const results = event.data.results || []

          const nextDomains = new Set<string>(currentState?.visited_domains || [])
          for (const r of results) {
            if (r?.url) nextDomains.add(urlHostname(r.url))
          }
          updateAgentState(event.agent_id, {
            searches: [
              ...(currentState?.searches || []),
              {
                query: event.data.query || '',
                results,
              },
            ],
            visited_domains: Array.from(nextDomains),
          })
        }
      }
    })

    // Handle agent search (legacy event name)
    const unsub5 = sseClient.on('agent_search', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'agent_search' && event.agent_id != null && event.data) {
        sseLog('[SSE] Agent search event:', event.agent_id)
        const store = useForecastStore.getState()
        const currentState = store.activeAgentStates[event.agent_id]
        updateAgentState(event.agent_id, {
          searches: [
            ...(currentState?.searches || []),
            {
              query: event.data.query || '',
              results: event.data.results || [],
            },
          ],
        })
      }
    })

    // Handle agent complete
    const unsub6 = sseClient.on('agent_complete', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'agent_complete') {
        // Prediction agents complete with agent_id, supervisor completes with agent_id=null.
        if (event.stage === 'prediction' && event.agent_id != null && event.data) {
          const output = event.data.output || {}

          // Update live agent state (final searches + trace)
          updateAgentState(event.agent_id, {
            status: 'complete',
            reasoning: event.data.reasoning_trace || '',
            searches: event.data.searches || [],
          })

          // Update agent predictions immediately (no need to wait for forecast_complete)
          const nextPrediction = {
            agent_id: event.agent_id,
            probability:
              typeof output.raw_probability === 'number'
                ? output.raw_probability
                : typeof output.probability === 'number'
                  ? output.probability
                  : 0,
            confidence: output.qualitative_confidence || output.confidence || 'medium',
            reasoning: output.reasoning || '',
            question_restatement: output.question_restatement,
            key_evidence: output.key_evidence || [],
            notes: output.notes,
            visited_urls: event.data.visited_urls || [],
            reasoning_trace: event.data.reasoning_trace || '',
          }

          const store = useForecastStore.getState()
          const existing = store.agentPredictions || []
          const idx = existing.findIndex((p) => p.agent_id === event.agent_id)
          const merged =
            idx >= 0
              ? existing.map((p) => (p.agent_id === event.agent_id ? { ...p, ...nextPrediction } : p))
              : [...existing, nextPrediction]
          setAgentPredictions(merged as any)
          return
        }

        if (event.agent_id != null) {
          updateAgentState(event.agent_id, { status: 'complete' })
        }
      }
    })

    // Handle supervisor reasoning
    const unsub7 = sseClient.on('supervisor_reasoning', (event: SSEEvent) => {
      const eventType = getEventType(event)
      if (eventType === 'supervisor_reasoning' && event.data) {
        setSupervisorReasoning(event.data.reasoning || '')
      }
    })

    // NOTE: We intentionally ignore supervisor `text_delta`.
    // For supervisor runs we request strict JSON schema output, so `text_delta` is usually raw JSON.
    // The user-facing “reasoning trace” should come from `reasoning_delta` + search events instead.
    const unsub8 = sseClient.on('text_delta', () => {})

    unsubscribeRef.current = [
      unsub1,
      unsub2,
      unsub3a,
      unsub3b,
      unsub3,
      unsub4a,
      unsub4,
      unsub5a,
      unsub5b,
      unsub5,
      unsub6,
      unsub7,
      unsub8,
    ]

    return () => {
      unsubscribeRef.current.forEach((unsub) => unsub())
      sseClient.disconnect()
    }
  }, [
    jobId,
    navigate,
    setCurrentJobId,
    setCurrentQuestionId,
    setAgentPredictions,
    setCurrentRuns,
    updateAgentState,
    setSupervisorReasoning,
    setSupervisorStageStarted,
    setPredictionStageStarted,
    setPredictionStageComplete,
    setSupervisorStageComplete,
    setFinalStageComplete,
    setFormatterStatus,
    setFormatterPreview,
    setFormatterSearch,
    setSupervisorSearches,
    setSupervisorVisitedDomains,
  ])
}

